import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export const SVEA_PRODUCTS = ['payment_link', 'invoice', 'part_payment', 'b2b_invoice'] as const;
export type SveaProduct = (typeof SVEA_PRODUCTS)[number];

export type SveaEnvironment = 'test' | 'production';

export interface SveaContactRequest {
  requestedAt: string;
  requestedByUserId: string;
  requestedByName: string;
  products: SveaProduct[];
}

export interface SveaStoredPaymentMethod {
  code: string;
  displayName: string;
  imageUrl?: string;
  extraFeeCents?: number;
}

export interface SveaLastTest {
  testedAt: string;
  success: boolean;
  error?: string;
  methods?: SveaStoredPaymentMethod[];
}

export interface SveaCredentialsStatus {
  sellerId: string | null;
  environment: SveaEnvironment | null;
  hasSecret: boolean;
  encryptionAvailable: boolean;
  lastTest: SveaLastTest | null;
  /** True only with encryption, seller id, environment, secret and a latest successful test. */
  enabled: boolean;
}

export interface SveaIntegrationResponse {
  /** False when this environment has no Svea lead inbox or no email key. */
  leadChannelConfigured: boolean;
  contactRequest: SveaContactRequest | null;
  workshop: {
    companyName: string;
    businessId: string | null;
    address: string | null;
    postalCode: string | null;
    city: string | null;
  };
  prefill: {
    contactName: string;
    contactEmail: string;
    contactPhone: string;
  };
  credentials: SveaCredentialsStatus;
}

export interface SveaContactRequestInput {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  products: SveaProduct[];
  message?: string;
  consent: true;
}

export interface SveaCredentialsInput {
  sellerId: string;
  environment: SveaEnvironment;
  secretKey?: string;
}

export interface SveaTestResponse {
  success: boolean;
  credentials: SveaCredentialsStatus;
}

const SVEA_QUERY_KEY = ['integrations', 'svea'] as const;

export function useSveaIntegration(enabled: boolean) {
  return useQuery({
    queryKey: SVEA_QUERY_KEY,
    queryFn: () => api.get<SveaIntegrationResponse>('/integrations/svea'),
    enabled,
  });
}

export function useSendSveaContactRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SveaContactRequestInput) =>
      api.post<{ contactRequest: SveaContactRequest | null }>('/integrations/svea/contact-request', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SVEA_QUERY_KEY });
    },
  });
}

export function useSaveSveaCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SveaCredentialsInput) =>
      api.put<SveaCredentialsStatus>('/integrations/svea/credentials', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SVEA_QUERY_KEY });
    },
  });
}

export function useTestSveaCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SveaTestResponse>('/integrations/svea/test', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SVEA_QUERY_KEY });
    },
  });
}

export function useDeleteSveaCredentials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<SveaCredentialsStatus>('/integrations/svea/credentials'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SVEA_QUERY_KEY });
    },
  });
}

/**
 * Whether the Svea payment link can be offered (#2978).
 *
 * Payment screens use this instead of {@link useSveaIntegration}: that one is
 * the settings view behind `settings.edit_workshop`, and a cashier who can
 * take payments but not edit the workshop got a 403 there and so never saw
 * the link. The key sits under the settings key, so saving or testing
 * credentials refreshes it too.
 */
export function useSveaPaymentLinkStatus(enabled: boolean) {
  return useQuery({
    queryKey: [...SVEA_QUERY_KEY, 'status'],
    queryFn: () => api.get<{ enabled: boolean }>('/integrations/svea/status'),
    enabled,
    staleTime: 60_000,
  });
}
